import { useState, useRef, useMemo, useEffect, useCallback, useLayoutEffect } from 'react';
import { useDevToolsStore } from '../../store/devToolsStore';
import { protocolFieldsOf } from '../../utils/requestProtocolFields';
import { sendRequest, isTauri } from '../../utils/tauri';
import { withUserNetwork } from '../../utils/requestNetworkOptions';
import { beginHttpEventStream, closeHttpEventStream } from '../../utils/httpEventStreams';
import { cancelHttpStream } from '../../utils/httpStreamTransport';
import { withheldSecretKeys } from '../../utils/scriptVariables';
import { withheldSecretValues } from '../../utils/sandboxRequestRedaction';
import { scriptOverlaySource } from '../../utils/scriptEnvOverlay';
import { wireHeadersBytes, utf8ByteLength } from '../../utils/formatBytes';
import { isModKey } from '../../utils/keyboard';
import { normalizeAuth } from '../../utils/requestAuth';
import { assembleRequestParts, authorizeRequestParts, resolveRequestAuth } from '../../utils/requestAssembly';
import { getResponseBodyEditorContent, getResponseRawText } from '../../utils/responseBody';
import { runScript } from '../../utils/scriptSandbox';
import { applyRequestMutations, preRequestScriptBody } from '../../utils/scriptRequestMutations';
import { announcePreRequestFailure, announceOAuthRenewalFailure } from '../../services/scriptErrors';
import { renewOAuthToken } from '../../services/oauthRenewal';
import { saveExportFile } from '../../utils/exportSave';
import { requestOptionId } from '../../utils/requestKinds';
import { recordFeature, ACTION } from '../../services/telemetry';
import { requestKeyFor } from '../../services/telemetry/features';

/** Send, cancel, history preview, the Send and History dropdowns, download and save. */
export function useRequestSend({
  t,
  url,
  auth,
  response,
  body,
  preRequestScript,
  initialData,
  collectionAuth,
  folderAuthChain,
  onSaveRequest,
  onAddToHistory,
  onUpdateResponse,
  history,
  onSelectFromHistory,
  loading,
  name,
  editable,
  showToast,
  params,
  headers,
  cookies,
  method,
  bodyType,
  rawBodyLanguage,
  formDataFields,
  setAuth,
  testScript,
  responseExamples,
  setTestResults,
  setTestScriptErrors,
  inflightRequestRef,
  cancelTokenRef,
  lastTabIdRef,
  tabFieldsRef,
  substituteVariables,
  notifyChange,
  sendHttpRequestRef,
  scriptLocalVarsRef,
  buildUrl,
  buildScriptScopes,
  buildEnvVariableMap,
  applyScriptEnvMutations,
}) {
  const [showSendDropdown, setShowSendDropdown] = useState(false);
  const sendDropdownRef = useRef(null);
  const sendDropdownTriggerRef = useRef(null);
  const [sendDropdownPosition, setSendDropdownPosition] = useState(null);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const historyDropdownRef = useRef(null);
  const historyDropdownTriggerRef = useRef(null);
  const [historyDropdownPosition, setHistoryDropdownPosition] = useState(null);
  // Which history entry the response viewer is currently showing ('current' = the latest run).
  const [activeHistoryId, setActiveHistoryId] = useState('current');
  // When previewing a past run, holds { timestamp } for the breadcrumb; null = showing current.
  const [historyPreview, setHistoryPreview] = useState(null);
  // Snapshot of the live tab state captured when entering preview, so X restores it.
  const previewSnapshotRef = useRef(null);
  // Stable per-request identity: a saved request keeps its backend/local id across reloads
  // and gets a new one on duplicate; an unsaved/duplicated tab falls back to its (fresh) tab id.
  const requestHistoryKey = useMemo(() => {
    const raw = initialData?.originalId ?? initialData?.id;
    return raw != null ? String(raw) : null;
  }, [initialData?.originalId, initialData?.id]);

  // DEPRECATED AND REMOVED (03.08.2026): per-request presence broadcasting.
  //
  // This pushed "I am viewing request X" to the workspace channel on every
  // request open, so teammates could watch which request you had focused. The
  // product decision is that realtime collaboration does not belong in the
  // request builder — seeing what someone else is doing here is surveillance,
  // not collaboration.
  //
  // It was also already write-only: the avatar stack that rendered viewers had
  // been removed earlier, and no client code read the context back. All it did
  // was tell the server, continuously, which request each person had open.
  //
  // The SERVER handlers stay (`presence:context` in workspace_channel.ex and
  // team_channel.ex). Phoenix crashes a channel on an unmatched handle_in, and
  // shipped clients (0.1.13) still push this — removing them would break the
  // workspace channel for everyone who has not updated.
  //
  // Presence itself is untouched: documents, boards and task detail still use
  // it, and the workspace roster still shows who is online.

  // Only entries made from exactly this request instance (matched by the per-request key).
  const requestHistory = useMemo(
    () => (requestHistoryKey
      ? (history || []).filter((h) => h.requestKey != null && String(h.requestKey) === requestHistoryKey)
      : []),
    [history, requestHistoryKey]
  );

  // Switching tabs/requests resets the active marker to the latest ("Current") run.
  useEffect(() => {
    setActiveHistoryId('current');
    setHistoryPreview(null);
    previewSnapshotRef.current = null;
  }, [initialData?.id]);

  // Restore the live request+response captured before previewing (no unsaved flag).
  const exitHistoryPreview = useCallback(() => {
    if (previewSnapshotRef.current) {
      onUpdateResponse?.(previewSnapshotRef.current);
      previewSnapshotRef.current = null;
    }
    setHistoryPreview(null);
    setActiveHistoryId('current');
  }, [onUpdateResponse]);

  // Non-destructively show a past run: snapshot the live state once, then apply the
  // resolved history entry through onUpdateResponse (which never marks the tab unsaved).
  const enterHistoryPreview = useCallback(async (item) => {
    if (!previewSnapshotRef.current) {
      const f = tabFieldsRef.current;
      previewSnapshotRef.current = {
        method: f.method, url: f.url, headers: f.headers, params: f.params,
        cookies: f.cookies, body: f.body, bodyType: f.bodyType, auth: f.auth,
        formDataFields: f.formDataFields, response,
      };
    }
    const data = await onSelectFromHistory?.(item, { resolveOnly: true });
    if (data) onUpdateResponse?.(data);
    setHistoryPreview({ timestamp: item.timestamp });
    setActiveHistoryId(item.id);
  }, [onSelectFromHistory, onUpdateResponse, response, tabFieldsRef]);

  // If the previewed entry disappears (history cleared or evicted), drop the breadcrumb
  // and restore the current request/response.
  useEffect(() => {
    if (historyPreview && !requestHistory.some((h) => h.id === activeHistoryId)) {
      exitHistoryPreview();
    }
  }, [requestHistory, historyPreview, activeHistoryId, exitHistoryPreview]);

  const sendHttpRequest = async () => {
    if (!url) return null;

    // Identifier-free usage count: an HTTP (or GraphQL-bodied) request was sent
    // today. Counted on SEND, not on tab open — an empty tab proves nothing.
    recordFeature(requestKeyFor(requestOptionId({ ...initialData, bodyType })));

    const pushConsoleEntry = useDevToolsStore.getState().pushEntry;

    const token = ++cancelTokenRef.current;
    inflightRequestRef.current = { method, url: buildUrl() };
    onUpdateResponse?.({ loading: true });
    // Reset test results on each new send
    setTestResults(null);
    setTestScriptErrors(null);
    const startTime = Date.now();
    let outboundHeaderBytes = 0;
    let outboundBodyBytes = 0;

    // ── Pre-request script ──────────────────────────────────────────────────
    // The script runs with what it set last time still visible (a token cache
    // reads its own token). Once it completes, what it set last time is
    // replaced by what it set now — nothing at all if the script is now empty,
    // so a deleted `pm.environment.set('base_url', …)` stops applying (owner
    // bug, 16.10.2026). The same, unedited script skipping a set (a cached
    // token) keeps the value. A failing script returns below, changing nothing.
    // Both keys: an unsaved tab's values were set under its tab id, and saving
    // gives it an originalId (requestHistoryKey) while the tab id stays.
    const overlaySources = (phase) => [...new Set([requestHistoryKey, initialData?.id]
      .filter((k) => k != null && k !== '')
      .map((k) => scriptOverlaySource(String(k), phase)))];
    const scriptPreRequest = tabFieldsRef.current.preRequestScript ?? '';
    // req.setHeader()/setBody()/setMethod() from the script — untrusted, applied
    // (and validated) as the last step before the send, see below.
    let scriptRequestMutations = null;
    if (scriptPreRequest.trim()) {
      const preResult = await runScript(scriptPreRequest, {
        environmentVariables: buildEnvVariableMap(),
        withheldKeys: withheldSecretKeys(buildScriptScopes()),
        withheldValues: withheldSecretValues(buildScriptScopes()),
        // `name`/`id` are what back pm.info.requestName / pm.info.requestId,
        // which the AI tool description advertises. Without them the worker
        // falls back to '' and the documented capability is permanently empty.
        // No headers on purpose — see the AI dry run, which matches this.
        // `body` is the body as typed (unsubstituted, as Bruno's req.getBody()
        // sees it); undefined for a multipart form.
        request: {
          url: buildUrl(),
          method,
          name,
          id: requestHistoryKey ?? '',
          body: preRequestScriptBody({ method, url, params, headers, cookies, body, bodyType, rawBodyLanguage, formDataFields }),
        },
        // pm.sendRequest — the imported-Postman token flow. Routed through the
        // app's own sender so the user's proxy, client certificates and TLS
        // settings apply to it exactly as they do to the request itself.
        sendHttp: (req) => sendRequest(withUserNetwork(req)),
      });

      if (preResult.errors.length > 0) {
        // The send is abandoned, so this MUST reach the user. showToast does
        // not: popup toasts are disabled app-wide, which made a failing
        // pre-request script look like a dead Send button (services/scriptErrors).
        announcePreRequestFailure(preResult.errors[0]);
        onUpdateResponse?.({ loading: false });
        return null;
      }

      // Apply pm.variables.set() results as highest-priority local scope for this send
      if (Object.keys(preResult.variables).length > 0) {
        scriptLocalVarsRef.current = { ...preResult.variables };
      }

      // Apply environment mutations from the script (COL-03).
      applyScriptEnvMutations(preResult.envMutations, overlaySources('pre'), { replace: true, script: scriptPreRequest });
      scriptRequestMutations = preResult.requestMutations || null;
    } else {
      applyScriptEnvMutations({}, overlaySources('pre'), { replace: true });
    }

    try {
      // Headers, cookies, params and body come from the module Run collection
      // uses too (utils/requestAssembly) — the two cannot drift apart again.
      const parts = assembleRequestParts(
        { method, url, params, headers, cookies, body, bodyType, rawBodyLanguage, formDataFields },
        { substituteVariables }
      );
      const { multipart } = parts;
      if (bodyType === 'form-data' && multipart && multipart.length === 0) {
        showToast(t('request.formDataEmpty'), 'warning');
        onUpdateResponse?.({ loading: false });
        return null;
      }

      // request → folder chain → collection, the same resolution the runner uses.
      const authScope = { folderAuthChain, collectionAuth };
      let authForSend = auth;
      const effectiveAuth = resolveRequestAuth(auth, authScope);
      if (effectiveAuth.type === 'oauth2') {
        try {
          // Covers the refresh-token grants AND client_credentials, which has
          // no refresh token and so was never renewed at all — see
          // services/oauthRenewal.
          const renewed = await renewOAuthToken(effectiveAuth.oauth2, { substituteVariables });
          if (renewed && normalizeAuth(auth, { forRequest: true }).type === 'oauth2') {
            authForSend = { ...auth, oauth2: { ...effectiveAuth.oauth2, ...renewed } };
            setAuth(authForSend);
            notifyChange({ auth: authForSend });
          }
        } catch (refreshErr) {
          // Not fatal: the stale token is still attached and the server gets to
          // have the last word. But it must be visible — a toast would not be
          // (see services/scriptErrors for why).
          announceOAuthRenewalFailure(refreshErr);
        }
      }

      const { headers: assembledHeaders, url: requestUrl } = await authorizeRequestParts(parts, {
        auth: resolveRequestAuth(authForSend, authScope),
        substituteVariables,
      });

      // The pre-request script's req.set*() changes go on LAST — after {{var}}
      // substitution and auth, so the script wins (as in Bruno), and never
      // substituted again, so a script cannot route a {{withheld}} secret into a
      // header. No URL change exists: req.setUrl() refuses (exfiltration).
      const {
        method: sentMethod,
        headers: authedHeaders,
        body: requestBody,
        notes: mutationNotes,
      } = applyRequestMutations(
        { method, headers: assembledHeaders, body: parts.body, multipart },
        scriptRequestMutations
      );
      if (mutationNotes.length > 0) {
        showToast(`Pre-request script: ${mutationNotes.join('; ')}`, 'warning');
      }

      outboundHeaderBytes = wireHeadersBytes(authedHeaders);
      outboundBodyBytes = requestBody ? utf8ByteLength(requestBody) : 0;

      const sentFromTabId = lastTabIdRef.current;
      // an answer of `text/event-stream` is streamed into the tab's log instead of timing out
      const eventStreamHooks = isTauri() ? beginHttpEventStream(sentFromTabId, { url: requestUrl, cancel: cancelHttpStream }) : undefined;
      const responseObj = await sendRequest(withUserNetwork({
        method: sentMethod,
        url: requestUrl,
        headers: authedHeaders,
        body: requestBody,
        multipart,
        stream: eventStreamHooks,
        isCancelled: () => token !== cancelTokenRef.current, // a cancelled >4 MB body stops being read
      }));

      const { text: displayBody } = getResponseBodyEditorContent(responseObj);

      const resHeadBytes = wireHeadersBytes(responseObj.headers || {});
      const rawDataStr = getResponseRawText(responseObj);
      const resBodyBytes =
        typeof responseObj.size === 'number' ? responseObj.size : utf8ByteLength(rawDataStr);

      const finalResponse = {
        ...responseObj,
        url: requestUrl,
        data: rawDataStr,
        statusText: responseObj.status_text || responseObj.statusText,
        byteMetrics: {
          responseHeaders: resHeadBytes,
          responseBody: resBodyBytes,
          requestHeaders: outboundHeaderBytes,
          requestBody: outboundBodyBytes,
        },
      };

      if (token !== cancelTokenRef.current) {
        if (responseObj.streaming) closeHttpEventStream(sentFromTabId, eventStreamHooks?.id); // nobody will show THIS one (a newer request's stream stays)
        return null; // request was cancelled
      }

      // ── Test script ─────────────────────────────────────────────────────────
      // an event stream has no body yet: a test script would assert against nothing
      const scriptTest = responseObj.streaming ? '' : (tabFieldsRef.current.testScript ?? '');
      // Same replace-after-completion as the pre-request phase. A streamed
      // response skips the phase entirely, so its values stay as they were.
      if (!responseObj.streaming && !scriptTest.trim()) {
        applyScriptEnvMutations({}, overlaySources('test'), { replace: true });
      }
      if (scriptTest.trim()) {
        const testResult = await runScript(scriptTest, {
          environmentVariables: buildEnvVariableMap(),
          withheldKeys: withheldSecretKeys(buildScriptScopes()),
          withheldValues: withheldSecretValues(buildScriptScopes()),
          request: { url: requestUrl, method: sentMethod, headers: authedHeaders, name, id: requestHistoryKey ?? '' },
          response: finalResponse,
        });
        recordFeature(ACTION.TEST_SCRIPT);   // identifier-free: a test script ran today

        setTestResults(testResult.tests);
        setTestScriptErrors(testResult.errors.length > 0 ? testResult.errors : null);

        // Apply any env mutations from the test script (COL-03). One that threw
        // part-way only adds what it set: it did not complete, so what it set
        // last time (a token from the last good login) is kept.
        applyScriptEnvMutations(testResult.envMutations, overlaySources('test'), {
          replace: testResult.errors.length === 0,
          script: scriptTest,
        });
      }

      onUpdateResponse?.({ response: finalResponse, loading: false });

      // Add to history
      onAddToHistory?.({
        method, url, headers, params, cookies, body, bodyType, formDataFields, auth,
        requestKey: requestHistoryKey,
      }, finalResponse);
      setActiveHistoryId('current'); // the just-sent response is now the "Current" one
      setHistoryPreview(null);
      previewSnapshotRef.current = null;

      pushConsoleEntry({
        method: sentMethod,
        url: requestUrl,
        status: finalResponse.status,
        statusText: finalResponse.statusText,
        durationMs: finalResponse.time,
        requestHeaders: authedHeaders,
        requestBody: requestBody,
        responseHeaders: finalResponse.headers || {},
        responseBody: displayBody,
        responseSizeBytes: resBodyBytes,
        timeline: finalResponse.timeline || { total: finalResponse.time },
      });

      // Show success toast
      if (finalResponse.status >= 200 && finalResponse.status < 300) {
        showToast(`Request successful (${finalResponse.status}) - ${finalResponse.time}ms`, 'success');
      } else {
        showToast(`Request completed with status ${finalResponse.status}`, 'warning');
      }

      return finalResponse;

    } catch (error) {
      if (token !== cancelTokenRef.current) return null; // request was cancelled

      const errorResponse = {
        status: 0,
        statusText: 'Error',
        headers: {},
        data: error.message,
        time: Date.now() - startTime,
        size: 0,
        byteMetrics: {
          responseHeaders: 0,
          responseBody: 0,
          requestHeaders: outboundHeaderBytes,
          requestBody: outboundBodyBytes,
        },
      };

      onUpdateResponse?.({ response: errorResponse, loading: false });

      // Add to history even for errors
      onAddToHistory?.({
        method, url, headers, params, cookies, body, bodyType, formDataFields, auth,
        requestKey: requestHistoryKey,
      }, errorResponse);
      setActiveHistoryId('current'); // the just-sent response is now the "Current" one
      setHistoryPreview(null);
      previewSnapshotRef.current = null;

      pushConsoleEntry({
        method,
        url: buildUrl(),
        status: 0,
        statusText: 'Error',
        durationMs: errorResponse.time,
        requestHeaders: {},
        requestBody: null,
        responseHeaders: {},
        responseBody: error.message,
        error: error.message,
      });

      // Show error toast
      showToast(`Request failed: ${error.message}`, 'error');
      return errorResponse;
    } finally {
      // Clear script-local variables after every send (success or error)
      scriptLocalVarsRef.current = {};
    }
  };

  sendHttpRequestRef.current = sendHttpRequest;

  useLayoutEffect(() => {
    if (!showSendDropdown) { setSendDropdownPosition(null); return; }
    const el = sendDropdownTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const MENU_WIDTH = 150;
    const gap = 6;
    const left = Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8);
    setSendDropdownPosition({ position: 'fixed', left, top: rect.bottom + gap, width: MENU_WIDTH, zIndex: 10000 });
  }, [showSendDropdown]);

  useEffect(() => {
    if (!showSendDropdown) return;
    const handleClickOutside = (e) => {
      if (sendDropdownRef.current && !sendDropdownRef.current.contains(e.target) &&
          sendDropdownTriggerRef.current && !sendDropdownTriggerRef.current.contains(e.target)) {
        setShowSendDropdown(false);
      }
    };
    const handleResize = () => setShowSendDropdown(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [showSendDropdown]);

  useLayoutEffect(() => {
    if (!showHistoryDropdown) { setHistoryDropdownPosition(null); return; }
    const el = historyDropdownTriggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const MENU_WIDTH = 360;
    const gap = 6;
    const left = Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
    setHistoryDropdownPosition({ position: 'fixed', left, top: rect.bottom + gap, width: MENU_WIDTH, zIndex: 10000 });
  }, [showHistoryDropdown]);

  useEffect(() => {
    if (!showHistoryDropdown) return;
    const handleClickOutside = (e) => {
      if (historyDropdownRef.current && !historyDropdownRef.current.contains(e.target) &&
          historyDropdownTriggerRef.current && !historyDropdownTriggerRef.current.contains(e.target)) {
        setShowHistoryDropdown(false);
      }
    };
    const handleResize = () => setShowHistoryDropdown(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [showHistoryDropdown]);

  const downloadResponse = async () => {
    setShowSendDropdown(false);
    const result = await sendHttpRequest();
    if (!result || result.status === 0) return;
    if (result.streaming) return; // an event stream has no body to save — it is shown as it arrives
    const body = typeof result.data === 'string' ? result.data : JSON.stringify(result.data ?? '');
    const contentType = (result.headers?.['content-type'] || result.headers?.['Content-Type'] || '').toLowerCase();
    const ext = contentType.includes('json') ? 'json' : contentType.includes('xml') ? 'xml' : contentType.includes('html') ? 'html' : contentType.includes('csv') ? 'csv' : 'txt';
    const hostname = (() => { try { return new URL(buildUrl()).hostname.replace(/\./g, '_'); } catch { return 'response'; } })();
    const filename = `${hostname}_${result.status}.${ext}`;
    await saveExportFile({ content: body, filename, format: ext === 'json' ? 'openapi_json' : null });
  };

  // Keyboard shortcuts: Ctrl+S save, Ctrl+Enter send. Ctrl+S runs the SAME save as the
  // button (it had its own copy, which dropped the gRPC / WebSocket fields).
  const saveRequestRef = useRef(null);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isModKey(e) && e.key === 's') {
        e.preventDefault();
        saveRequestRef.current?.();
        return;
      }
      if (isModKey(e) && e.key === 'Enter') {
        e.preventDefault();
        if (url && !loading) {
          sendHttpRequestRef.current?.();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [url, loading, sendHttpRequestRef]);

  const saveRequest = useCallback(() => {
    if (!editable) return;
    const currentData = {
      name, method, url, headers, params, cookies, body, bodyType, rawBodyLanguage, formDataFields, auth, preRequestScript, testScript, mock: { responseExamples },
      // gRPC / WebSocket: the builders keep their fields on the tab (initialData); without these
      // a saved request reopened empty after the tab closed (card 5258c71d).
      ...protocolFieldsOf(initialData),
    };
    onSaveRequest?.(currentData);
    showToast('Request saved successfully!', 'success');
  }, [name, method, url, headers, params, cookies, body, bodyType, rawBodyLanguage, formDataFields, auth, preRequestScript, testScript, responseExamples, onSaveRequest, showToast, editable, initialData]);
  saveRequestRef.current = saveRequest;

  return {
    showSendDropdown,
    setShowSendDropdown,
    sendDropdownRef,
    sendDropdownTriggerRef,
    sendDropdownPosition,
    showHistoryDropdown,
    setShowHistoryDropdown,
    historyDropdownRef,
    historyDropdownTriggerRef,
    historyDropdownPosition,
    activeHistoryId,
    historyPreview,
    requestHistoryKey,
    requestHistory,
    exitHistoryPreview,
    enterHistoryPreview,
    sendHttpRequest,
    downloadResponse,
    saveRequest,
  };
}
